import React from 'react';
import { Dropdown } from 'semantic-ui-react';
import AccessPointTypes from 'models/types/AccessPointTypes';

interface UserAccessPoint {
    accessPoint: AccessPointTypes;
    accessLevel: number;
    isActive: boolean;
}

interface RoleTemplate {
    name: string;
    description: string;
    accessPoints: UserAccessPoint[];
}

interface Props {
    onRoleSelect: (accessPoints: UserAccessPoint[]) => void;
    disabled?: boolean;
}

const RoleSelector: React.FC<Props> = ({ onRoleSelect, disabled = false }) => {
    const roleTemplates: RoleTemplate[] = [
        {
            name: 'Owner',
            description: 'Full access to all features',
            accessPoints: [
                { accessPoint: AccessPointTypes.Dashboard, accessLevel: 3, isActive: true },
                { accessPoint: AccessPointTypes.Calendar, accessLevel: 3, isActive: true },
                { accessPoint: AccessPointTypes.Inventory, accessLevel: 3, isActive: true },
                { accessPoint: AccessPointTypes.Reports, accessLevel: 3, isActive: true },
                { accessPoint: AccessPointTypes.Settings, accessLevel: 3, isActive: true },
            ],
        },
        {
            name: 'Manager',
            description: 'Can manage most operations except sensitive settings',
            accessPoints: [
                { accessPoint: AccessPointTypes.Dashboard, accessLevel: 2, isActive: true },
                { accessPoint: AccessPointTypes.Calendar, accessLevel: 2, isActive: true },
                { accessPoint: AccessPointTypes.Inventory, accessLevel: 2, isActive: true },
                { accessPoint: AccessPointTypes.Reports, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Settings, accessLevel: 1, isActive: true },
            ],
        },
        {
            name: 'Technician',
            description: 'Can update work orders and view inventory',
            accessPoints: [
                { accessPoint: AccessPointTypes.Dashboard, accessLevel: 2, isActive: true },
                { accessPoint: AccessPointTypes.Calendar, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Inventory, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Reports, accessLevel: 1, isActive: false },
                { accessPoint: AccessPointTypes.Settings, accessLevel: 1, isActive: false },
            ],
        },
        {
            name: 'Front Desk',
            description: 'Customer interaction and scheduling',
            accessPoints: [
                { accessPoint: AccessPointTypes.Dashboard, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Calendar, accessLevel: 2, isActive: true },
                { accessPoint: AccessPointTypes.Inventory, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Reports, accessLevel: 1, isActive: false },
                { accessPoint: AccessPointTypes.Settings, accessLevel: 1, isActive: false },
            ],
        },
        {
            name: 'Viewer',
            description: 'Read-only access',
            accessPoints: [
                { accessPoint: AccessPointTypes.Dashboard, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Calendar, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Inventory, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Reports, accessLevel: 1, isActive: true },
                { accessPoint: AccessPointTypes.Settings, accessLevel: 1, isActive: false },
            ],
        },
    ];

    const dropdownOptions = roleTemplates.map((role) => ({
        key: role.name,
        value: role.name,
        text: role.name,
        description: role.description,
    }));

    const handleRoleChange = (event: React.SyntheticEvent, { value }: any) => {
        const selectedRole = roleTemplates.find((role) => role.name === value);
        if (selectedRole) {
            onRoleSelect(selectedRole.accessPoints);
        }
    };

    return (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Quick Role Assignment</label>
            <Dropdown
                placeholder="Select a predefined role..."
                fluid
                selection
                options={dropdownOptions}
                onChange={handleRoleChange}
                disabled={disabled}
                clearable
            />
            <p className="text-xs text-gray-500 mt-1">Selecting a role will override current access point settings</p>
        </div>
    );
};

export default RoleSelector;
